// npm i xlsx
import * as XLSX from 'xlsx';
import { encode } from 'gpt-tokenizer';

/**
 * Convert an XLSX file into a structured JSON representation.
 *
 * @param {Buffer|Uint8Array|ArrayBuffer} input - XLSX file contents
 * @param {object} [opts]
 * @param {boolean} [opts.includeRows2d=false] - include a dense 2D array per sheet (can be huge)
 * @param {boolean} [opts.includeBlankCells=false] - include blank cells in sparse list (usually false)
 * @returns {object} structured JSON
 */

type optType = { includeRows2d?: boolean; includeBlankCells?: boolean };
type InputBuffer = Buffer | Uint8Array | ArrayBuffer;
type CellInfo = {
  address: string;
  r: number;
  c: number;
  type: XLSX.CellObject['t'] | null;
  valueRaw: XLSX.CellObject['v'] | null;
  display: string | null;
  formula: string | null;
  numFmt: string | null;
};
type MergeInfo = {
  start: { r: number; c: number; a1: string };
  end: { r: number; c: number; a1: string };
  rangeA1: string;
};
type SheetInfo = {
  name: string;
  ref: string;
  dimensions: {
    start: { r: number; c: number; a1: string };
    end: { r: number; c: number; a1: string };
    maxRow: number;
    maxCol: number;
  };
  merges: MergeInfo[];
  cells: CellInfo[];
  rows2d?: unknown[][];
};
type StructuredXlsx = {
  meta: { sheetNames: string[] };
  sheets: SheetInfo[];
};
function normalizeToBuffer(input: InputBuffer): Buffer {
  if (Buffer.isBuffer(input)) return input;
  if (input instanceof ArrayBuffer) return Buffer.from(input);
  return Buffer.from(input);
}

export function xlsxToStructuredJson(input: InputBuffer, opts: optType = {}) {
  const { includeRows2d = false, includeBlankCells = false } = opts;

  // cellDates:true keeps dates as JS Date when possible (instead of raw numbers)
  // cellNF:true includes number format strings where available
  // cellText:true makes .w (formatted text) more consistently available
  const workbook = XLSX.read(normalizeToBuffer(input), {
    type: 'buffer',
    cellDates: true,
    cellNF: true,
    cellText: true
  });
  const out: StructuredXlsx = {
    meta: {
      sheetNames: workbook.SheetNames.slice()
    },
    sheets: []
  };

  for (const sheetName of workbook.SheetNames) {
    const ws = workbook.Sheets[sheetName];
    const ref = ws['!ref'] || 'A1:A1';
    const range = XLSX.utils.decode_range(ref);

    // merges are ranges like { s: {r,c}, e: {r,c} }
    const merges: MergeInfo[] = (ws['!merges'] || []).map((m) => ({
      start: { r: m.s.r, c: m.s.c, a1: XLSX.utils.encode_cell(m.s) },
      end: { r: m.e.r, c: m.e.c, a1: XLSX.utils.encode_cell(m.e) },
      rangeA1: XLSX.utils.encode_range(m)
    }));

    // Build sparse cells list
    const cells: CellInfo[] = [];

    // Iterate through worksheet keys (A1, B2, etc). Special keys start with "!"
    for (const key of Object.keys(ws)) {
      if (key.startsWith('!')) continue;

      const cell = ws[key];
      if (!cell) continue;

      // Skip blanks unless asked
      // cell.t is type: 's' string, 'n' number, 'b' boolean, 'd' date, 'e' error, 'z' stub/blank
      if (!includeBlankCells && (cell.t === 'z' || cell.v === undefined)) {
        continue;
      }

      const decoded = XLSX.utils.decode_cell(key);

      cells.push({
        address: key, // e.g. "C5"
        r: decoded.r, // 0-based row index
        c: decoded.c, // 0-based col index
        type: cell.t || null, // 's'|'n'|'b'|'d'|'e'|'z'
        valueRaw: cell.v ?? null, // raw value (Date, number, string, boolean)
        display: cell.w ?? null, // formatted text if available
        formula: cell.f ?? null, // formula string if present
        numFmt: cell.z ?? null // number format string if present
      });
    }

    // Optional: dense 2D array (values only, using formatted display where possible)
    let rows2d: SheetInfo['rows2d'] = undefined;
    if (includeRows2d) {
      // sheet_to_json with header:1 gives a 2D array of row arrays
      // defval controls blank cells. If includeBlankCells, fill with null.
      rows2d = XLSX.utils.sheet_to_json(ws, {
        header: 1,
        raw: false, // prefer formatted strings
        defval: includeBlankCells ? null : undefined,
        blankrows: false
      });
    }

    out.sheets.push({
      name: sheetName,
      ref,
      dimensions: {
        start: {
          r: range.s.r,
          c: range.s.c,
          a1: XLSX.utils.encode_cell(range.s)
        },
        end: {
          r: range.e.r,
          c: range.e.c,
          a1: XLSX.utils.encode_cell(range.e)
        },
        maxRow: range.e.r,
        maxCol: range.e.c
      },
      merges,
      cells, // sparse list
      rows2d // optional dense matrix
    });
  }

  const numberOfTokens = encode(JSON.stringify(out)).length;
  return numberOfTokens;
}

// Example usage:
// const obj = await s3.getObject({ Bucket, Key }).promise();
// const json = xlsxToStructuredJson(obj.Body as Buffer, { includeRows2d: false });
// console.log(JSON.stringify(json, null, 2));
